import { useMemo, useState } from "react";
import { Botao, GrupoDeOpcoes, Opcao } from "../ui";
import { normalizarParaBusca } from "../../nucleo/texto";
import type { ModuloDoCatalogo, RecursoDoCatalogo } from "../../nucleo/implantacoes";

export interface PropsDosRecursosPlus {
  /** O catálogo ATIVO inteiro — os 25 vendáveis, não um recorte pelos módulos marcados. */
  recursos: RecursoDoCatalogo[];
  modulos: ModuloDoCatalogo[];
  selecionados: string[];
  /** Os módulos marcados na seção de cima. Muda o texto do item, nunca a existência dele. */
  modulosContratados: string[];
  aoAlternar: (recurso: RecursoDoCatalogo) => void;
}

/** Quantos itens a lista mostra antes de virar rolagem interna — nunca rolagem da página. */
const TETO_VISIVEL = 8;

function nomeDoModulo(modulos: ModuloDoCatalogo[], chave: string | null): string {
  if (chave === null) return "Todos os módulos";
  return modulos.find((modulo) => modulo.chave === chave)?.nome ?? chave;
}

/**
 * A seção "Recursos Plus contratados" da tela 1.
 *
 * **Ela busca, não empilha.** São 25 itens vendáveis: a lista inteira aberta é uma parede de
 * caixinhas em que ninguém acha nada, e foi por isso que a consultoria pediu busca. O padrão da
 * tela é campo vazio e nenhuma opção; digitou, aparece o que casa; quem não sabe o nome abre a
 * lista toda num clique — e ela rola dentro da própria caixa.
 *
 * **O que está escolhido nunca some.** O bloco dos selecionados fica acima da busca e independe
 * do filtro: sem ele, digitar uma segunda consulta esconderia o que já foi marcado, e a pessoa
 * perderia a conta do que vendeu — o defeito clássico deste tipo de campo.
 *
 * A busca casa nome do recurso E nome do módulo, sem acento e sem caixa: procurar "analytics"
 * traz os quatro do módulo mesmo sem saber o nome de nenhum.
 */
export function RecursosPlusContratados({
  recursos,
  modulos,
  selecionados,
  modulosContratados,
  aoAlternar,
}: PropsDosRecursosPlus) {
  const [consulta, setConsulta] = useState("");
  const [listaAberta, setListaAberta] = useState(false);

  const escolhidos = useMemo(
    () => recursos.filter((recurso) => selecionados.includes(recurso.chave)),
    [recursos, selecionados],
  );

  const procurando = consulta.trim().length > 0;

  const encontrados = useMemo(() => {
    if (!procurando) return recursos;
    const alvo = normalizarParaBusca(consulta);
    return recursos.filter((recurso) => {
      const modulo = nomeDoModulo(modulos, recurso.modulo_chave);
      const texto = normalizarParaBusca(`${recurso.nome} ${recurso.descricao ?? ""} ${modulo}`);
      return texto.includes(alvo);
    });
  }, [consulta, modulos, procurando, recursos]);

  // A lista aparece quando há consulta ou quando a pessoa pediu para ver tudo. Fora isso a
  // seção é só a busca e o que já foi escolhido — que é o pedido literal da consultoria:
  // "sem ficar todos na tela como opção".
  const mostrarLista = procurando || listaAberta;

  return (
    <>
      <p className="formulario-secao__dica" data-testid="contagem-de-recursos-plus">
        {recursos.length} Recursos Plus à venda no catálogo
        {escolhidos.length > 0 ? ` · ${escolhidos.length} escolhido${escolhidos.length > 1 ? "s" : ""}` : ""}.
      </p>

      {escolhidos.length > 0 ? (
        <ul className="recursos-plus__escolhidos" data-testid="recursos-plus-escolhidos">
          {escolhidos.map((recurso) => (
            <li key={recurso.chave} className="recursos-plus__escolhido">
              <span className="recursos-plus__nome">{recurso.nome}</span>
              <span className="etiqueta">{nomeDoModulo(modulos, recurso.modulo_chave)}</span>
              <button
                type="button"
                className="recursos-plus__tirar"
                onClick={() => aoAlternar(recurso)}
              >
                {/* O nome vai no rótulo acessível: "Tirar" sozinho, repetido cinco vezes, não
                    diz a ninguém qual dos cinco o botão tira. */}
                <span aria-hidden="true">Tirar</span>
                <span className="sr-only">Tirar {recurso.nome}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="recursos-plus__busca">
        <div className="campo">
          <label className="campo__rotulo" htmlFor="busca-de-recurso-plus">
            Buscar Recurso Plus
          </label>
          <input
            id="busca-de-recurso-plus"
            type="search"
            className="campo__controle"
            data-testid="busca-de-recurso-plus"
            placeholder="Nome do recurso ou do módulo — brindes, analytics, estoque…"
            value={consulta}
            onChange={(evento) => setConsulta(evento.target.value)}
          />
        </div>
        <Botao
          type="button"
          variante="fantasma"
          data-testid="alternar-lista-de-recursos-plus"
          onClick={() => {
            setListaAberta((aberta) => !aberta);
            // Abrir a lista com uma consulta escrita mostraria o filtro, não a lista.
            if (!listaAberta) setConsulta("");
          }}
        >
          {listaAberta ? "Ocultar a lista" : `Ver os ${recursos.length}`}
        </Botao>
      </div>

      {mostrarLista ? (
        <>
          <p className="sr-only" role="status">
            {encontrados.length} Recursos Plus na lista.
          </p>
          {encontrados.length === 0 ? (
            <p className="lead" data-testid="sem-resultado-de-recurso-plus">
              Nenhum Recurso Plus com esse nome. Confira a grafia — ou o catálogo, no console.
            </p>
          ) : (
            <div
              className="recursos-plus__lista"
              // Rolagem só quando ela é necessária: com quatro itens a caixa não deve ter
              // altura de oito.
              style={
                encontrados.length > TETO_VISIVEL ? { maxHeight: "21rem", overflowY: "auto" } : undefined
              }
            >
              <GrupoDeOpcoes rotulo="Recursos Plus do catálogo" rotuloOculto>
                {encontrados.map((recurso) => {
                  const modulo = nomeDoModulo(modulos, recurso.modulo_chave);
                  const exigeModulo =
                    recurso.modulo_chave !== null &&
                    !modulosContratados.includes(recurso.modulo_chave);
                  const partes = [
                    recurso.descricao ?? "",
                    // O item de módulo não contratado continua escolhível. A alternativa —
                    // escondê-lo, como antes — é o defeito que a consultoria apontou: quem
                    // vendeu o Plus vendeu o módulo, e a tela marca os dois em vez de sumir
                    // com um deles. Dizer isso ANTES do clique é o que evita a surpresa.
                    exigeModulo ? `marca o módulo ${modulo} junto` : "",
                  ].filter(Boolean);
                  return (
                    <Opcao
                      key={recurso.chave}
                      tipo="checkbox"
                      name="recurso-plus"
                      value={recurso.chave}
                      titulo={recurso.nome}
                      descricao={partes.length > 0 ? partes.join(" · ") : undefined}
                      etiqueta={modulo}
                      checked={selecionados.includes(recurso.chave)}
                      onChange={() => aoAlternar(recurso)}
                    />
                  );
                })}
              </GrupoDeOpcoes>
            </div>
          )}
        </>
      ) : null}
    </>
  );
}
